import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { UserButton, useAuth } from "@clerk/clerk-react";
import { useBuilding, COMPANY_BUILDING } from "../context/BuildingContext";
import {
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  ChartPieIcon,
  DocumentDuplicateIcon,
  UsersIcon,
  CogIcon,
  ClipboardDocumentListIcon,
  ReceiptPercentIcon,
  BuildingLibraryIcon,
  BanknotesIcon,
  MicrophoneIcon,
  CalendarDaysIcon,
  TagIcon,
  ArrowUpTrayIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { BUILD_VERSION } from "../version";
import BreadcrumbBar from "./common/BreadcrumbBar";

interface LayoutProps {
  children: React.ReactNode;
}

const navigation = [
  { name: "Dashboard", href: "/dashboard", icon: HomeIcon },
  { name: "Calendar", href: "/dashboard/calendar", icon: CalendarDaysIcon },
  { name: "Buildings", href: "/dashboard/buildings", icon: BuildingOfficeIcon },
  { name: "Units", href: "/dashboard/units", icon: BuildingLibraryIcon },
  { name: "Contacts", href: "/dashboard/contacts", icon: UsersIcon },
  {
    name: "Financial Records",
    href: "/dashboard/financial-records",
    icon: DocumentTextIcon,
  },
  { name: "Categories", href: "/dashboard/categories", icon: TagIcon },
  { name: "Meetings", href: "/dashboard/meetings", icon: MicrophoneIcon },
  {
    name: "Journal",
    href: "/dashboard/journal",
    icon: ClipboardDocumentListIcon,
  },
  { name: "Banking", href: "/dashboard/banking", icon: BanknotesIcon },
  { name: "Budgets", href: "/dashboard/budgets", icon: ReceiptPercentIcon },
  {
    name: "Statements",
    href: "/dashboard/statements",
    icon: DocumentDuplicateIcon,
  },
  { name: "Reports", href: "/dashboard/reports", icon: ChartPieIcon },
  {
    name: "Building Compliance",
    href: "/dashboard/compliance",
    icon: ShieldCheckIcon,
  },
  { name: "Users", href: "/dashboard/users", icon: UsersIcon },
  { name: "Settings", href: "/dashboard/settings", icon: CogIcon },
  {
    name: "Import",
    href: "/dashboard/import/freeagent",
    icon: ArrowUpTrayIcon,
  },
  {
    name: "Mgmt Fees",
    href: "/dashboard/management-fees",
    icon: ReceiptPercentIcon,
  },
];

const COMPANY_HIDDEN_NAV = new Set([
  "Units",
  "Statements",
  "Meetings",
  "Budgets",
]);

const NavLinks: React.FC<{ onClick?: () => void }> = ({ onClick }) => {
  const location = useLocation();
  const { currentBuilding } = useBuilding();
  const isCompany = currentBuilding?.id === "COMPANY";

  return (
    <nav className="flex-1 px-2 py-4 bg-white space-y-1">
      {navigation
        .filter((item) => !isCompany || !COMPANY_HIDDEN_NAV.has(item.name))
        .map((item) => {
          const isActive =
            location.pathname === item.href ||
            (item.href !== "/dashboard" &&
              location.pathname.startsWith(item.href));
          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={onClick}
              className={`
              group flex items-center px-2 py-2 text-sm font-medium rounded-md min-h-[44px] md:min-h-0
              ${
                isActive
                  ? "bg-primary-100 text-primary-700"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              }
            `}
            >
              <item.icon
                className={`mr-3 flex-shrink-0 h-6 w-6 ${
                  isActive
                    ? "text-primary-700"
                    : "text-gray-400 group-hover:text-gray-500"
                }`}
                aria-hidden="true"
              />
              {item.name}
            </Link>
          );
        })}
    </nav>
  );
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const { isLoaded, isSignedIn } = useAuth();
  const { currentBuilding, isLoading: isBuildingLoading } = useBuilding();

  // Redirect to building selection if signed in but no building selected
  useEffect(() => {
    if (isLoaded && isSignedIn && !isBuildingLoading && !currentBuilding) {
      navigate("/select-building", { replace: true });
    }
  }, [isLoaded, isSignedIn, isBuildingLoading, currentBuilding, navigate]);

  return (
    <div className="h-full min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div
        className={`fixed inset-0 z-40 bg-gray-600 bg-opacity-75 transition-opacity ease-linear duration-300 ${
          sidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setSidebarOpen(false)}
      ></div>

      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white transform transition ease-in-out duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between h-16 px-4 sm:px-6 border-b border-gray-200">
          <div className="flex items-center">
            <div className="flex flex-col items-start">
              <img className="h-8 w-auto" src="/logo.png" alt="Ledgiventa" />
            </div>
            <h1 className="ml-2 text-xl font-semibold text-gray-900">
              Ledgiventa
            </h1>
            <div className="text-xs text-gray-400 mt-1">{BUILD_VERSION}</div>
          </div>
          <button
            type="button"
            className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            onClick={() => setSidebarOpen(false)}
          >
            <span className="sr-only">Close sidebar</span>
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        <NavLinks onClick={() => setSidebarOpen(false)} />
      </div>

      {/* Desktop sidebar */}
      <div className="hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col">
        <div className="flex-1 flex flex-col min-h-0 bg-white border-r border-gray-200">
          <div className="flex items-center h-16 px-4 bg-white border-b border-gray-200">
            <div className="flex flex-col items-start">
              <img className="h-8 w-auto" src="/logo.png" alt="Ledgiventa" />
              <div className="text-xs text-gray-400 mt-1">
                Version: {BUILD_VERSION}
              </div>
            </div>
            <h1 className="ml-2 text-xl font-semibold text-gray-900">
              Ledgiventa
            </h1>
          </div>
          <div className="flex-1 flex flex-col overflow-y-auto">
            <NavLinks />
          </div>
        </div>
      </div>

      <div className="md:pl-64 flex flex-col">
        <div className="sticky top-0 z-10 md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3 bg-gray-50">
          <button
            type="button"
            className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        <main className="flex-1">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              <div className="flex items-center justify-between mb-2">
                <BreadcrumbBar />
                <div className="flex-shrink-0 ml-4">
                  <UserButton />
                </div>
              </div>
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
